/*
 * Copyright (C) 2026 Fluxer Contributors
 *
 * This file is part of Fluxer.
 *
 * Fluxer is free software: you can redistribute it and/or modify
 * it under the terms of the GNU Affero General Public License as published by
 * the Free Software Foundation, either version 3 of the License, or
 * (at your option) any later version.
 *
 * Fluxer is distributed in the hope that it will be useful,
 * but WITHOUT ANY WARRANTY; without even the implied warranty of
 * MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE. See the
 * GNU Affero General Public License for more details.
 *
 * You should have received a copy of the GNU Affero General Public License
 * along with Fluxer. If not, see <https://www.gnu.org/licenses/>.
 */

import * as ChannelActionCreators from '@app/actions/ChannelActionCreators';
import * as ModalActionCreators from '@app/actions/ModalActionCreators';
import {modal} from '@app/actions/ModalActionCreators';
import * as ToastActionCreators from '@app/actions/ToastActionCreators';
import * as UnsavedChangesActionCreators from '@app/actions/UnsavedChangesActionCreators';
import {ChannelPermissionsUpdateFailedModal} from '@app/components/alerts/ChannelPermissionsUpdateFailedModal';
import {Input} from '@app/components/form/Input';
import styles from '@app/components/modals/channel_tabs/ChannelPermissionsTab.module.css';
import {AddOverridePopout} from '@app/components/modals/shared/AddOverridePopout';
import {
	DEFAULT_ROLE_COLOR_HEX,
	getPermissionState,
	getRoleColor,
	PermissionOverwriteCategory,
	type PermissionState,
	PermissionStateButtons,
} from '@app/components/modals/shared/PermissionComponents';
import {Avatar} from '@app/components/uikit/Avatar';
import {Button} from '@app/components/uikit/button/Button';
import {openRoleContextMenu} from '@app/components/uikit/context_menu/RoleContextMenu';
import {Tooltip} from '@app/components/uikit/tooltip/Tooltip';
import {Logger} from '@app/lib/Logger';
import type {UserRecord} from '@app/records/UserRecord';
import ChannelStore from '@app/stores/ChannelStore';
import GuildMemberStore from '@app/stores/GuildMemberStore';
import GuildStore from '@app/stores/GuildStore';
import LayerManager from '@app/stores/LayerManager';
import MobileLayoutStore from '@app/stores/MobileLayoutStore';
import PermissionLayoutStore from '@app/stores/PermissionLayoutStore';
import PermissionStore from '@app/stores/PermissionStore';
import SettingsSidebarStore from '@app/stores/SettingsSidebarStore';
import UserStore from '@app/stores/UserStore';
import * as PermissionUtils from '@app/utils/PermissionUtils';
import {
	FloatingFocusManager,
	flip,
	offset,
	shift,
	useClick,
	useDismiss,
	useFloating,
	useInteractions,
	useRole,
} from '@floating-ui/react';
import {Permissions} from '@fluxer/constants/src/ChannelConstants';
import {Trans, useLingui} from '@lingui/react/macro';
import {
	CaretRightIcon,
	GridFourIcon,
	ListIcon,
	MagnifyingGlassIcon,
	PlusIcon,
	RowsIcon,
	TrashIcon,
	UsersIcon,
} from '@phosphor-icons/react';
import {clsx} from 'clsx';
import {matchSorter} from 'match-sorter';
import {observer} from 'mobx-react-lite';
import type React from 'react';
import {useCallback, useEffect, useLayoutEffect, useMemo, useState} from 'react';

const logger = new Logger('ChannelPermissionsTab');

interface PermissionOverwrite {
	id: string;
	type: 0 | 1;
	allow: bigint;
	deny: bigint;
}

const CHANNEL_PERMISSIONS_TAB_ID = 'permissions';

const OverwriteItem: React.FC<{
	overwrite: PermissionOverwrite;
	name: string;
	color?: number;
	user?: UserRecord | null;
	roleId?: string | null;
	isSelected: boolean;
	isEveryone: boolean;
	onClick: () => void;
	guildId: string;
}> = observer(({overwrite, name, color, user, roleId, isSelected, isEveryone, onClick, guildId}) => {
	const handleContextMenu = useCallback(
		(event: React.MouseEvent<HTMLButtonElement>) => {
			if (!roleId) return;
			openRoleContextMenu(event, roleId);
		},
		[roleId],
	);

	return (
		<button
			type="button"
			className={clsx(styles.overwriteItem, {[styles.overwriteItemSelected]: isSelected})}
			onClick={onClick}
			onContextMenu={roleId ? handleContextMenu : undefined}
		>
			{overwrite.type === 0 && !isEveryone ? (
				<div
					className={styles.roleDot}
					style={{backgroundColor: color === 0 ? DEFAULT_ROLE_COLOR_HEX : getRoleColor(color || 0)}}
				/>
			) : overwrite.type === 1 && user ? (
				<Avatar user={user} size={12} className={styles.overwriteIcon} guildId={guildId} />
			) : (
				<UsersIcon className={styles.overwriteIcon} />
			)}
			<span className={styles.overwriteName}>{name}</span>
		</button>
	);
});

const ChannelPermissionsTab: React.FC<{channelId: string}> = observer(({channelId}) => {
	const {t, i18n} = useLingui();
	const channel = ChannelStore.getChannel(channelId);
	const parentChannel = ChannelStore.getChannel(channel?.parentId || '');
	const guild = GuildStore.getGuild(channel?.guildId || '');
	const currentUser = UserStore.currentUser;
	const isMobile = MobileLayoutStore.enabled;

	const overrideOwnerId = useMemo(() => `channel-permissions-${channelId}`, [channelId]);

	const [selectedOverwriteId, setSelectedOverwriteId] = useState<string | null>(null);
	const [mobileShowEditor, setMobileShowEditor] = useState(false);
	const [overwriteUpdates, setOverwriteUpdates] = useState<Map<string, PermissionOverwrite>>(new Map());
	const [deletedOverwriteIds, setDeletedOverwriteIds] = useState<Set<string>>(new Set());
	const [newOverwriteIds, setNewOverwriteIds] = useState<Set<string>>(new Set());
	const [permissionSearchQuery, setPermissionSearchQuery] = useState('');

	const [isAddOverrideOpen, setIsAddOverrideOpen] = useState(false);
	const {
		refs: addOverrideRefs,
		floatingStyles: addOverrideFloatingStyles,
		context: addOverrideContext,
	} = useFloating({
		open: isAddOverrideOpen,
		onOpenChange: setIsAddOverrideOpen,
		placement: 'bottom-start',
		middleware: [offset(8), flip(), shift({padding: 8})],
	});

	const {getReferenceProps: getAddOverrideReferenceProps, getFloatingProps: getAddOverrideFloatingProps} =
		useInteractions([
			useClick(addOverrideContext),
			useDismiss(addOverrideContext, {escapeKey: false}),
			useRole(addOverrideContext),
		]);

	const canManageChannels = PermissionStore.can(Permissions.MANAGE_CHANNELS, {guildId: channel?.guildId || ''});
	const canManageRoles = PermissionStore.can(Permissions.MANAGE_ROLES, {guildId: channel?.guildId || ''});

	useEffect(() => {
		const key = `channel-permissions-add-override-${channelId}`;
		if (isAddOverrideOpen) {
			LayerManager.addLayer('popout', key, () => setIsAddOverrideOpen(false));
		}
		return () => {
			LayerManager.removeLayer('popout', key);
		};
	}, [isAddOverrideOpen, channelId]);

	const currentUserPermissions = useMemo(() => {
		if (!guild || !currentUser || !channel) return 0n;
		return PermissionUtils.computePermissions(currentUser.id, channel.toJSON());
	}, [guild, currentUser, channel]);

	const currentUserMember = useMemo(() => {
		if (!guild || !currentUser) return null;
		return GuildMemberStore.getMember(guild.id, currentUser.id);
	}, [guild, currentUser]);

	const wouldRemoveOwnPermission = useCallback(
		(permission: bigint, overwriteId: string, overwriteType: 0 | 1): boolean => {
			if (!guild || !currentUser || !channel || !currentUserMember) return false;
			if (guild.isOwner(currentUser.id)) return false;

			const isEveryoneOverwrite = overwriteId === guild.id;
			const isMemberOverwrite = overwriteType === 1 && overwriteId === currentUser.id;
			const isRoleOverwrite = overwriteType === 0 && currentUserMember.roles.has(overwriteId);

			if (!isEveryoneOverwrite && !isMemberOverwrite && !isRoleOverwrite) return false;

			const currentOverwrite = channel.permissionOverwrites[overwriteId];
			if (!currentOverwrite) return false;

			const hasAllowForPermission = (currentOverwrite.allow & permission) === permission;
			if (!hasAllowForPermission) return false;

			const modifiedOverwrites: Record<string, {id: string; type: 0 | 1; allow: bigint; deny: bigint}> = {};
			for (const [id, ow] of Object.entries(channel.permissionOverwrites)) {
				if (id === overwriteId) {
					modifiedOverwrites[id] = {
						id,
						type: ow.type as 0 | 1,
						allow: ow.allow & ~permission,
						deny: ow.deny,
					};
				} else {
					modifiedOverwrites[id] = {
						id,
						type: ow.type as 0 | 1,
						allow: ow.allow,
						deny: ow.deny,
					};
				}
			}

			const permissionsWithoutAllow = PermissionUtils.computePermissions(
				currentUser.id,
				channel.toJSON(),
				modifiedOverwrites,
			);

			return (permissionsWithoutAllow & permission) !== permission;
		},
		[guild, currentUser, channel, currentUserMember],
	);

	const overwrites = useMemo(() => {
		if (!channel || !guild) return [];

		const everyoneOverwrite = channel.permissionOverwrites[guild.id];
		const otherOverwrites = Object.entries(channel.permissionOverwrites)
			.filter(([id]) => id !== guild.id && !deletedOverwriteIds.has(id))
			.map(([id, ow]) => ({
				id,
				type: ow.type as 0 | 1,
				allow: ow.allow,
				deny: ow.deny,
			}));

		for (const newId of newOverwriteIds) {
			if (!otherOverwrites.find((ow) => ow.id === newId)) {
				const update = overwriteUpdates.get(newId);
				if (update) {
					otherOverwrites.push(update);
				}
			}
		}

		const roles: Array<PermissionOverwrite> = [];
		const members: Array<PermissionOverwrite> = [];

		for (const ow of otherOverwrites) {
			if (ow.type === 0) {
				roles.push(ow);
			} else {
				members.push(ow);
			}
		}

		roles.sort((a, b) => {
			const roleA = guild.roles[a.id];
			const roleB = guild.roles[b.id];
			if (!roleA || !roleB) return 0;
			if (roleA.position !== roleB.position) {
				return roleB.position - roleA.position;
			}
			return BigInt(a.id) < BigInt(b.id) ? -1 : 1;
		});

		members.sort((a, b) => (BigInt(a.id) < BigInt(b.id) ? -1 : 1));

		const result: Array<PermissionOverwrite> = [...roles, ...members];

		result.push(
			everyoneOverwrite
				? {
						id: guild.id,
						type: 0 as const,
						allow: everyoneOverwrite.allow,
						deny: everyoneOverwrite.deny,
					}
				: {
						id: guild.id,
						type: 0 as const,
						allow: 0n,
						deny: 0n,
					},
		);

		return result;
	}, [channel, guild, deletedOverwriteIds, newOverwriteIds, overwriteUpdates]);

	const overwritesWithUpdates = useMemo(() => {
		return overwrites.map((ow) => {
			const update = overwriteUpdates.get(ow.id);
			return update || ow;
		});
	}, [overwrites, overwriteUpdates]);

	const selectedOverwrite = useMemo(() => {
		if (!selectedOverwriteId) return null;
		return overwritesWithUpdates.find((ow) => ow.id === selectedOverwriteId) || null;
	}, [selectedOverwriteId, overwritesWithUpdates]);

	const getPermissionDisabledReason = useCallback(
		(permission: bigint): string | undefined => {
			if (!guild || !currentUser) return;

			if (!canManageChannels) return t`You need Manage Channels to edit these permissions`;
			if (!canManageRoles) return t`You need Manage Roles to edit these permissions`;
			if (guild.isOwner(currentUser.id)) return;

			if ((currentUserPermissions & permission) !== permission) {
				return t`You cannot grant a permission you don't have in this channel`;
			}

			if (selectedOverwrite && wouldRemoveOwnPermission(permission, selectedOverwrite.id, selectedOverwrite.type)) {
				return t`You cannot remove this permission because it would remove it from yourself`;
			}

			return;
		},
		[
			guild,
			currentUser,
			currentUserPermissions,
			canManageChannels,
			canManageRoles,
			selectedOverwrite,
			wouldRemoveOwnPermission,
		],
	);

	const getPermissionWarning = useCallback(
		(permission: bigint): string | undefined => {
			if (!guild || !currentUser || !selectedOverwrite) return;
			if (guild.isOwner(currentUser.id)) return;
			if (wouldRemoveOwnPermission(permission, selectedOverwrite.id, selectedOverwrite.type)) {
				return t`You cannot remove this permission because it would remove it from yourself`;
			}
			return;
		},
		[guild, currentUser, selectedOverwrite, wouldRemoveOwnPermission],
	);

	useLayoutEffect(() => {
		if (overwrites.length > 0 && !selectedOverwriteId) {
			setSelectedOverwriteId(overwrites[0].id);
		}
	}, [overwrites, selectedOverwriteId]);

	useEffect(() => {
		if (!guild) {
			return;
		}
		const userOverwriteIds = overwrites.filter((ow) => ow.type === 1).map((ow) => ow.id);
		if (userOverwriteIds.length > 0) {
			GuildMemberStore.ensureMembersLoaded(guild.id, userOverwriteIds).catch((error) => {
				logger.error('Failed to ensure members', error);
			});
		}
	}, [guild, overwrites]);

	const hasUnsavedChanges = useMemo(
		() => overwriteUpdates.size > 0 || deletedOverwriteIds.size > 0 || newOverwriteIds.size > 0,
		[overwriteUpdates, deletedOverwriteIds, newOverwriteIds],
	);

	useEffect(() => {
		UnsavedChangesActionCreators.setUnsavedChanges(CHANNEL_PERMISSIONS_TAB_ID, hasUnsavedChanges);
	}, [hasUnsavedChanges]);

	const handleSave = useCallback(async () => {
		if (!channel || !canManageChannels || !canManageRoles) return;

		try {
			const updatedOverwrites = overwritesWithUpdates
				.filter((ow) => !deletedOverwriteIds.has(ow.id))
				.filter((ow) => {
					if (ow.id === guild?.id && ow.allow === 0n && ow.deny === 0n) {
						return false;
					}
					return true;
				})
				.map((ow): {id: string; type: 0 | 1; allow: string; deny: string} => ({
					id: ow.id,
					type: ow.type,
					allow: ow.allow.toString(),
					deny: ow.deny.toString(),
				}));

			await ChannelActionCreators.updatePermissionOverwrites(channel.id, updatedOverwrites);
			setOverwriteUpdates(new Map());
			setDeletedOverwriteIds(new Set());
			setNewOverwriteIds(new Set());

			ToastActionCreators.createToast({
				type: 'success',
				children: <Trans>Channel access updated</Trans>,
			});
		} catch (_error) {
			ModalActionCreators.push(modal(() => <ChannelPermissionsUpdateFailedModal />));
		}
	}, [channel, canManageChannels, canManageRoles, overwritesWithUpdates, guild, deletedOverwriteIds]);

	const handleReset = useCallback(() => {
		setOverwriteUpdates(new Map());
		setDeletedOverwriteIds(new Set());
		setNewOverwriteIds(new Set());
		if (guild) {
			setSelectedOverwriteId(guild.id);
		}
	}, [guild]);

	useEffect(() => {
		UnsavedChangesActionCreators.setTabData(CHANNEL_PERMISSIONS_TAB_ID, {
			onReset: handleReset,
			onSave: handleSave,
			isSubmitting: false,
		});
	}, [handleReset, handleSave]);

	useEffect(() => {
		return () => {
			UnsavedChangesActionCreators.clearUnsavedChanges(CHANNEL_PERMISSIONS_TAB_ID);
		};
	}, []);

	const handleOverwriteUpdate = useCallback(
		(overwriteId: string, updates: Partial<PermissionOverwrite>) => {
			setOverwriteUpdates((prev) => {
				const newMap = new Map(prev);
				const current = newMap.get(overwriteId) || overwrites.find((o) => o.id === overwriteId);
				if (!current) return prev;

				const merged = {...current, ...updates};

				const originalOverwrite = channel?.permissionOverwrites[overwriteId];
				const isNewOverwrite = newOverwriteIds.has(overwriteId);

				if (isNewOverwrite) {
					newMap.set(overwriteId, merged);
					return newMap;
				}

				if (originalOverwrite) {
					const hasChanges = merged.allow !== originalOverwrite.allow || merged.deny !== originalOverwrite.deny;

					if (hasChanges) {
						newMap.set(overwriteId, merged);
					} else {
						newMap.delete(overwriteId);
					}
				} else {
					const hasPermissions = merged.allow !== 0n || merged.deny !== 0n;

					if (hasPermissions) {
						newMap.set(overwriteId, merged);
					} else {
						newMap.delete(overwriteId);
					}
				}

				return newMap;
			});
		},
		[overwrites, channel, newOverwriteIds],
	);

	const applyPermissionState = useCallback(
		(permissions: Array<bigint>, state: PermissionState, baseAllow: bigint, baseDeny: bigint) => {
			let newAllow = baseAllow;
			let newDeny = baseDeny;

			for (const permission of permissions) {
				if (state === 'ALLOW') {
					newAllow |= permission;
					newDeny &= ~permission;
				} else if (state === 'DENY') {
					newDeny |= permission;
					newAllow &= ~permission;
				} else {
					newAllow &= ~permission;
					newDeny &= ~permission;
				}
			}

			return {allow: newAllow, deny: newDeny};
		},
		[],
	);

	const handlePermissionChange = useCallback(
		(permission: bigint, state: PermissionState) => {
			if (!selectedOverwrite) return;

			const {allow, deny} = applyPermissionState([permission], state, selectedOverwrite.allow, selectedOverwrite.deny);
			handleOverwriteUpdate(selectedOverwrite.id, {allow, deny});
		},
		[selectedOverwrite, handleOverwriteUpdate, applyPermissionState],
	);

	const permissionSpecs = useMemo(() => {
		if (!channel) return [];
		return PermissionUtils.generateChannelPermissionSpecs(i18n, channel.type);
	}, [channel, i18n]);

	const filteredPermissionSpecs = useMemo(() => {
		if (!permissionSearchQuery) return permissionSpecs;

		return permissionSpecs
			.map((spec) => {
				const filteredPermissions = matchSorter(spec.permissions, permissionSearchQuery, {
					keys: ['title', 'description'],
				});

				if (filteredPermissions.length === 0) return null;

				return {
					...spec,
					permissions: filteredPermissions,
				};
			})
			.filter((spec): spec is PermissionUtils.PermissionSpec => spec !== null);
	}, [permissionSpecs, permissionSearchQuery]);

	const allPermissionsState = useMemo(() => {
		if (!selectedOverwrite || filteredPermissionSpecs.length === 0) return;

		const allPermissions = filteredPermissionSpecs.flatMap((spec) => spec.permissions.map((p) => p.flag));
		if (allPermissions.length === 0) return;

		const firstState = getPermissionState(allPermissions[0], selectedOverwrite.allow, selectedOverwrite.deny);
		const allSameState = allPermissions.every(
			(perm) => getPermissionState(perm, selectedOverwrite.allow, selectedOverwrite.deny) === firstState,
		);

		return allSameState ? firstState : undefined;
	}, [selectedOverwrite, filteredPermissionSpecs]);

	const handleSetAllPermissions = useCallback(
		(state: PermissionState) => {
			if (!selectedOverwrite || !guild || !currentUser) return;

			const isOwner = guild.isOwner(currentUser.id);

			const allPermissions = permissionSpecs.flatMap((spec) => spec.permissions.map((p) => p.flag));
			const allowedPermissions = allPermissions.filter((perm) => {
				if (isOwner) return true;
				return (currentUserPermissions & perm) === perm;
			});

			const {allow, deny} = applyPermissionState(
				allowedPermissions,
				state,
				selectedOverwrite.allow,
				selectedOverwrite.deny,
			);
			handleOverwriteUpdate(selectedOverwrite.id, {allow, deny});
		},
		[
			selectedOverwrite,
			permissionSpecs,
			handleOverwriteUpdate,
			applyPermissionState,
			guild,
			currentUser,
			currentUserPermissions,
		],
	);

	const isSyncedWithParent = useMemo(() => {
		if (!channel || !channel.parentId || !parentChannel || !parentChannel.isGuildCategory()) return null;

		const channelOverwrites = channel.permissionOverwrites || {};
		const parentOverwrites = parentChannel.permissionOverwrites || {};

		const channelKeys = Object.keys(channelOverwrites).sort();
		const parentKeys = Object.keys(parentOverwrites).sort();

		if (channelKeys.length !== parentKeys.length) return false;
		if (channelKeys.join(',') !== parentKeys.join(',')) return false;

		for (const key of channelKeys) {
			const channelOw = channelOverwrites[key];
			const parentOw = parentOverwrites[key];

			if (!parentOw) return false;
			if (channelOw.type !== parentOw.type) return false;
			if (channelOw.allow !== parentOw.allow) return false;
			if (channelOw.deny !== parentOw.deny) return false;
		}

		return true;
	}, [channel, parentChannel]);

	const handleSyncWithParent = useCallback(async () => {
		if (!channel || !parentChannel || !canManageChannels || !canManageRoles) return;

		const parentOverwrites = parentChannel.permissionOverwrites || {};
		const updatedOverwrites = Object.entries(parentOverwrites).map(
			([id, ow]): {id: string; type: 0 | 1; allow: string; deny: string} => ({
				id,
				type: ow.type as 0 | 1,
				allow: ow.allow.toString(),
				deny: ow.deny.toString(),
			}),
		);

		try {
			await ChannelActionCreators.updatePermissionOverwrites(channel.id, updatedOverwrites);
			ToastActionCreators.createToast({
				type: 'success',
				children: <Trans>Channel synced with parent category</Trans>,
			});
		} catch (_error) {
			ModalActionCreators.push(modal(() => <ChannelPermissionsUpdateFailedModal />));
		}
	}, [channel, parentChannel, canManageChannels, canManageRoles]);

	const handleAddOverride = useCallback((id: string, type: 0 | 1, _name: string) => {
		const newOverwrite: PermissionOverwrite = {
			id,
			type,
			allow: 0n,
			deny: 0n,
		};

		setNewOverwriteIds((prev) => new Set(prev).add(id));
		setOverwriteUpdates((prev) => {
			const newMap = new Map(prev);
			newMap.set(id, newOverwrite);
			return newMap;
		});
		setSelectedOverwriteId(id);
	}, []);

	const handleDeleteOverride = useCallback(() => {
		if (!selectedOverwrite || !guild || selectedOverwrite.id === guild.id) return;

		const futureOverwrites = overwritesWithUpdates.filter((o) => o.id !== selectedOverwrite.id);
		const currentIndex = overwritesWithUpdates.findIndex((o) => o.id === selectedOverwrite.id);

		const nextOverwrite = futureOverwrites[currentIndex] ?? futureOverwrites[0];

		if (newOverwriteIds.has(selectedOverwrite.id)) {
			setNewOverwriteIds((prev) => {
				const newSet = new Set(prev);
				newSet.delete(selectedOverwrite.id);
				return newSet;
			});
		} else {
			setDeletedOverwriteIds((prev) => new Set(prev).add(selectedOverwrite.id));
		}

		setOverwriteUpdates((prev) => {
			const newMap = new Map(prev);
			newMap.delete(selectedOverwrite.id);
			return newMap;
		});
		setSelectedOverwriteId(nextOverwrite?.id ?? null);
	}, [selectedOverwrite, guild, overwritesWithUpdates, newOverwriteIds]);

	const getOverwriteName = useCallback(
		(overwrite: PermissionOverwrite): string => {
			if (!guild) return '';
			if (overwrite.id === guild.id) return '@everyone';
			if (overwrite.type === 0) {
				const role = guild.roles[overwrite.id];
				return role?.name || 'Unknown Role';
			}
			const user = UserStore.getUser(overwrite.id);
			return user?.username || 'Unknown Member';
		},
		[guild],
	);

	const getOverwriteColor = useCallback(
		(overwrite: PermissionOverwrite): number | undefined => {
			if (!guild || overwrite.type !== 0) return;
			const role = guild.roles[overwrite.id];
			return role?.color;
		},
		[guild],
	);

	const getOverwriteUser = useCallback((overwrite: PermissionOverwrite): UserRecord | null => {
		if (overwrite.type !== 1) return null;
		return UserStore.getUser(overwrite.id) || null;
	}, []);

	const handleMobileOverwriteSelect = useCallback((overwriteId: string) => {
		setSelectedOverwriteId(overwriteId);
		setMobileShowEditor(true);
	}, []);

	const handleMobileBack = useCallback(() => {
		setMobileShowEditor(false);
	}, []);

	const existingOverwriteIds = useMemo(() => {
		return new Set(overwritesWithUpdates.map((o) => o.id));
	}, [overwritesWithUpdates]);

	const isEveryoneSelected = selectedOverwrite?.id === guild?.id;

	const sidebarContent = useMemo(() => {
		if (!channel || !guild || !currentUser) return null;

		return (
			<div>
				<div className={styles.leftTitle} style={{padding: '6px 8px'}}>
					<Trans>Access Overrides</Trans>
				</div>
				<div ref={addOverrideRefs.setReference} {...getAddOverrideReferenceProps()} style={{padding: '0 8px 8px 8px'}}>
					<Button
						variant="secondary"
						small={true}
						disabled={!canManageChannels || !canManageRoles}
						leftIcon={<PlusIcon size={18} weight="bold" />}
						onClick={() => setIsAddOverrideOpen(!isAddOverrideOpen)}
					>
						<Trans>Add Override</Trans>
					</Button>
				</div>
				{isAddOverrideOpen && (
					<FloatingFocusManager context={addOverrideContext} modal={true}>
						<div
							ref={addOverrideRefs.setFloating}
							style={{
								...addOverrideFloatingStyles,
								zIndex: 99999,
								width:
									addOverrideRefs.reference.current && 'offsetWidth' in addOverrideRefs.reference.current
										? (addOverrideRefs.reference.current as {offsetWidth: number}).offsetWidth
										: 'auto',
								left: addOverrideFloatingStyles.left,
							}}
							{...getAddOverrideFloatingProps()}
						>
							<AddOverridePopout
								guildId={guild.id}
								existingOverwriteIds={existingOverwriteIds}
								onSelect={handleAddOverride}
								onClose={() => setIsAddOverrideOpen(false)}
							/>
						</div>
					</FloatingFocusManager>
				)}

				<div style={{padding: '0 8px 8px 8px'}}>
					{overwritesWithUpdates.map((overwrite) => {
						const name = getOverwriteName(overwrite);
						const color = getOverwriteColor(overwrite);
						const user = getOverwriteUser(overwrite);
						const isSelected = selectedOverwriteId === overwrite.id;
						const isEveryone = overwrite.id === guild.id;
						const roleId = overwrite.type === 0 ? overwrite.id : null;

						return (
							<OverwriteItem
								key={overwrite.id}
								overwrite={overwrite}
								name={name}
								color={color}
								user={user}
								roleId={roleId}
								isSelected={isSelected}
								isEveryone={isEveryone}
								onClick={() => setSelectedOverwriteId(overwrite.id)}
								guildId={guild.id}
							/>
						);
					})}
				</div>
			</div>
		);
	}, [
		channel,
		guild,
		currentUser,
		selectedOverwriteId,
		overwritesWithUpdates,
		canManageChannels,
		canManageRoles,
		isAddOverrideOpen,
		addOverrideContext,
		addOverrideFloatingStyles,
		getAddOverrideFloatingProps,
		getAddOverrideReferenceProps,
		handleAddOverride,
		getOverwriteName,
		getOverwriteColor,
		getOverwriteUser,
		addOverrideRefs.reference,
		existingOverwriteIds,
	]);

	useEffect(() => {
		if (isMobile || !channel || !guild || !currentUser) return;
		if (!SettingsSidebarStore.hasOverride) {
			SettingsSidebarStore.setOverride(overrideOwnerId, sidebarContent, {defaultOn: true});
		}
		return () => {
			if (SettingsSidebarStore.ownerId === overrideOwnerId) {
				SettingsSidebarStore.clearOverride(overrideOwnerId);
			}
		};
	}, [overrideOwnerId, isMobile]);

	useEffect(() => {
		if (isMobile || !channel || !guild || !currentUser) return;
		if (SettingsSidebarStore.ownerId === overrideOwnerId && sidebarContent) {
			SettingsSidebarStore.updateOverride(overrideOwnerId, sidebarContent);
		}
	}, [sidebarContent, channel, guild, currentUser, overrideOwnerId, isMobile]);

	const isComfyLayout = PermissionLayoutStore.isComfy;
	const isGridLayout = PermissionLayoutStore.isGrid;
	const permissionsScrollerKey = useMemo(
		() => `channel-permissions-right-scroller-${isComfyLayout ? 'comfy' : 'dense'}-${isGridLayout ? 'grid' : 'single'}`,
		[isComfyLayout, isGridLayout],
	);

	if (!channel || !guild || !currentUser) return null;

	if (isMobile && !mobileShowEditor) {
		return (
			<div className={styles.container}>
				{isSyncedWithParent !== null && (
					<div
						className={clsx(
							styles.syncBanner,
							isSyncedWithParent ? styles.syncBannerSynced : styles.syncBannerUnsynced,
						)}
					>
						<div>
							{isSyncedWithParent ? (
								<Trans>
									This channel is synced with the parent category <strong>{parentChannel?.name}</strong>.
								</Trans>
							) : (
								<Trans>
									This channel is not synced with the parent category <strong>{parentChannel?.name}</strong>.
								</Trans>
							)}
						</div>
						{!isSyncedWithParent && (
							<Button small={true} onClick={handleSyncWithParent} disabled={!canManageChannels || !canManageRoles}>
								<Trans>Sync with Category</Trans>
							</Button>
						)}
					</div>
				)}
				<div className={styles.mobileOverrideList}>
					<div className={styles.mobileListHeader}>
						<h2 className={styles.mobileListTitle}>
							<Trans>Access Overrides</Trans>
						</h2>
						<Button
							variant="secondary"
							small={true}
							leftIcon={<PlusIcon size={18} weight="bold" />}
							onClick={() => setIsAddOverrideOpen(!isAddOverrideOpen)}
							disabled={!canManageChannels || !canManageRoles}
						>
							<Trans>Add Override</Trans>
						</Button>
					</div>
					{isAddOverrideOpen && (
						<FloatingFocusManager context={addOverrideContext} modal={true}>
							<div
								ref={addOverrideRefs.setFloating}
								style={{
									...addOverrideFloatingStyles,
									zIndex: 99999,
								}}
								{...getAddOverrideFloatingProps()}
							>
								<AddOverridePopout
									guildId={guild.id}
									existingOverwriteIds={existingOverwriteIds}
									onSelect={handleAddOverride}
									onClose={() => setIsAddOverrideOpen(false)}
								/>
							</div>
						</FloatingFocusManager>
					)}
					<div className={styles.mobileOverrides}>
						{overwritesWithUpdates.map((overwrite) => {
							const name = getOverwriteName(overwrite);
							const color = getOverwriteColor(overwrite);
							const user = getOverwriteUser(overwrite);
							const isEveryone = overwrite.id === guild.id;
							const roleId = overwrite.type === 0 ? overwrite.id : null;

							return (
								<button
									key={overwrite.id}
									type="button"
									className={styles.mobileOverrideItem}
									onClick={() => handleMobileOverwriteSelect(overwrite.id)}
									onContextMenu={roleId ? (event) => openRoleContextMenu(event, roleId) : undefined}
								>
									{overwrite.type === 0 && !isEveryone ? (
										<div
											className={styles.roleDot}
											style={{
												backgroundColor: color === 0 ? DEFAULT_ROLE_COLOR_HEX : getRoleColor(color || 0),
											}}
										/>
									) : overwrite.type === 1 && user ? (
										<Avatar user={user} size={12} className={styles.overwriteIcon} guildId={guild.id} />
									) : (
										<UsersIcon className={styles.overwriteIcon} />
									)}
									<span className={styles.mobileOverrideName}>{name}</span>
									<CaretRightIcon className={styles.mobileOverrideChevron} size={20} weight="bold" />
								</button>
							);
						})}
					</div>
				</div>
			</div>
		);
	}

	return (
		<div className={styles.container}>
			{isSyncedWithParent !== null && (
				<div
					className={clsx(styles.syncBanner, isSyncedWithParent ? styles.syncBannerSynced : styles.syncBannerUnsynced)}
				>
					<div>
						{isSyncedWithParent ? (
							<Trans>
								This channel is synced with the parent category <strong>{parentChannel?.name}</strong>.
							</Trans>
						) : (
							<Trans>
								This channel is not synced with the parent category <strong>{parentChannel?.name}</strong>.
							</Trans>
						)}
					</div>
					{!isSyncedWithParent && (
						<Button small={true} onClick={handleSyncWithParent} disabled={!canManageChannels || !canManageRoles}>
							<Trans>Sync with Category</Trans>
						</Button>
					)}
				</div>
			)}
			<div className={styles.grid}>
				<div className={styles.right}>
					<div className={styles.rightScroller} key={permissionsScrollerKey}>
						{selectedOverwrite && (
							<>
								{isMobile && (
									<div className={styles.mobileBackRow}>
										<Button variant="secondary" small={true} onClick={handleMobileBack}>
											<Trans>Back to Overrides</Trans>
										</Button>
									</div>
								)}
								<div className={styles.sectionRow}>
									<div className={styles.sectionHeader}>
										<h2 className={styles.sectionTitle}>
											<Trans>Edit Access for {getOverwriteName(selectedOverwrite)}</Trans>
										</h2>
										<p className={styles.subtleText}>
											{isEveryoneSelected ? (
												<Trans>Configure base access for this channel</Trans>
											) : selectedOverwrite?.type === 0 ? (
												<Trans>Configure overrides for this role</Trans>
											) : (
												<Trans>Configure overrides for this member</Trans>
											)}
										</p>
									</div>
									{!isEveryoneSelected && (
										<Button
											variant="secondary"
											small={true}
											leftIcon={<TrashIcon size={18} />}
											onClick={handleDeleteOverride}
											disabled={!canManageChannels || !canManageRoles}
										>
											<Trans>Remove Override</Trans>
										</Button>
									)}
								</div>

								<div className={styles.sectionRow}>
									<div className={styles.permHeaderRow}>
										<p className={styles.permHelp}>
											<Trans>Use these buttons to quickly set all permissions.</Trans>
										</p>
										<PermissionStateButtons
											currentState={allPermissionsState}
											onStateChange={handleSetAllPermissions}
											disabled={!canManageChannels || !canManageRoles}
										/>
									</div>
								</div>

								<div className={styles.permSearchRow}>
									<Input
										type="text"
										placeholder={t`Search Permissions...`}
										value={permissionSearchQuery}
										onChange={(e) => setPermissionSearchQuery(e.target.value)}
										leftIcon={<MagnifyingGlassIcon size={16} weight="bold" />}
										className={styles.permSearchInput}
									/>
									<div className={styles.layoutButtons}>
										<Tooltip text={PermissionLayoutStore.isComfy ? t`Dense layout` : t`Comfy layout`}>
											<button
												type="button"
												className={styles.layoutButton}
												onClick={() => PermissionLayoutStore.toggleLayoutMode()}
												aria-label={
													PermissionLayoutStore.isComfy ? t`Switch to dense layout` : t`Switch to comfy layout`
												}
											>
												{PermissionLayoutStore.isComfy ? (
													<RowsIcon size={20} weight="bold" />
												) : (
													<ListIcon size={20} weight="bold" />
												)}
											</button>
										</Tooltip>
										<Tooltip text={PermissionLayoutStore.isGrid ? t`Single column` : t`Two columns`}>
											<button
												type="button"
												className={styles.layoutButton}
												onClick={() => PermissionLayoutStore.toggleGridMode()}
												aria-label={
													PermissionLayoutStore.isGrid ? t`Switch to single column` : t`Switch to two columns`
												}
											>
												<GridFourIcon size={20} weight={PermissionLayoutStore.isGrid ? 'fill' : 'bold'} />
											</button>
										</Tooltip>
									</div>
								</div>

								<div className={styles.permCategories}>
									{filteredPermissionSpecs.map((spec, index) => (
										<PermissionOverwriteCategory
											key={spec.title}
											spec={spec}
											allow={selectedOverwrite.allow}
											deny={selectedOverwrite.deny}
											onPermissionChange={handlePermissionChange}
											disabled={!canManageChannels || !canManageRoles}
											getPermissionDisabledReason={getPermissionDisabledReason}
											getPermissionWarning={getPermissionWarning}
											isFirst={index === 0}
										/>
									))}
									{filteredPermissionSpecs.length === 0 && permissionSearchQuery && (
										<div className={styles.emptyState}>
											<Trans>No permissions found</Trans>
										</div>
									)}
								</div>
							</>
						)}
					</div>
				</div>
			</div>
		</div>
	);
});

export default ChannelPermissionsTab;
